import type { LucideIcon } from 'lucide-react'
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'
import React from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip'
import './toolbar-button.css'

type ToolbarButtonProps = {
	// Polymorphic icon - accepts Lucide icons, custom components, or pre-rendered elements
	icon: LucideIcon | React.ComponentType<{ className?: string; style?: CSSProperties }> | React.ReactElement
	// Style to apply to the icon (for transforms like flip/rotate)
	iconStyle?: CSSProperties
	tooltip: string
	isActive?: boolean
	onClick?: () => void
	disabled?: boolean
	className?: string
	children?: ReactNode // For overlays like status dots or color swatches
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'disabled' | 'className' | 'children'>

export function ToolbarButton({
								  icon,
								  iconStyle,
								  tooltip,
								  isActive = false,
								  onClick,
								  disabled,
								  className,
								  children,
								  ...rest
							  }: ToolbarButtonProps) {
	// Render icon based on type
	const renderIcon = () => {
		// Case 1: Pre-rendered element (e.g., <EraserIcon />)
		if (React.isValidElement(icon)) {
			const existingStyle = (icon.props as any)?.style || {}
			return React.cloneElement(icon, {
				style: { ...existingStyle, ...iconStyle },
			} as any)
		}

		// Case 2: Component (LucideIcon or custom component)
		const Icon = icon as React.ComponentType<{ className?: string; style?: CSSProperties }>
		return <Icon style={iconStyle} />
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					onClick={onClick}
					disabled={disabled}
					data-active={isActive}
					className={`toolbar-button ${className ?? ''}`.trim()}
					style={!isActive ? { color: 'var(--icon-muted)' } : undefined}
					{...rest}
				>
					{renderIcon()}
					{children}
				</button>
			</TooltipTrigger>
			<TooltipContent side="right">{tooltip}</TooltipContent>
		</Tooltip>
	)
}
